/**
 * SettlementContext – Provides settlement-specific state to all settlement screens.
 *
 * Tracks:
 *  - Which estate we're working on (estateId)
 *  - Whether this is a planner path (existing EoE user) or clean slate
 *  - Death certificate status
 *  - Estate owner info (deceased name, etc.)
 *  - Current executor's role
 *  - Other executors on the same estate (for multi-executor awareness)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { apiService } from '../utils/api';

export type SettlementPath = 'planner' | 'clean-slate' | null;
export type DeathCertStatus = 'none' | 'pending' | 'approved' | 'rejected';

export interface SettlementEstateInfo {
  estateId: string;
  ownerName: string;
  ownerEmail?: string;
  dateOfPassing?: string;
  path: SettlementPath;
  deathCertStatus: DeathCertStatus;
}

export interface CoExecutor {
  userId: string;
  name: string;
  email: string;
  lastActive?: string;
}

/** Mirrors GET /api/settlement/:estateId/completion-status `checks` object */
export interface CompletionChecks {
  allPhasesComplete: boolean;
  debtsSettled: boolean;
  assetsDistributed: boolean;
  financialReconciliationZero: boolean;
  beneficiaryApprovals: boolean;
  courtDischargeUploaded: boolean;
  balanceCents: number;
  ledgerEntryCount: number;
}

interface SettlementContextValue {
  /** Current estate info, null if not loaded yet */
  estate: SettlementEstateInfo | null;
  /** Other executors on this estate */
  coExecutors: CoExecutor[];
  /** Whether we're loading estate data */
  isLoading: boolean;
  /** Last error */
  error: string | null;
  /** Refresh estate data from the server */
  refresh: () => Promise<void>;
  /** Whether the death cert is approved (shortcut) */
  isDeathCertApproved: boolean;
  /** Whether this is a planner path estate (shortcut) */
  isPlannerPath: boolean;
  /** Estate settled and server-enforced read-only */
  isArchived: boolean;
  settledAt: string | null;
  canClose: boolean;
  completionChecks: CompletionChecks | null;
  closureMissing: string[];
  /** Reload completion / archive status */
  refreshClosure: () => Promise<void>;
}

const SettlementContext = createContext<SettlementContextValue | undefined>(undefined);

export function useSettlement(): SettlementContextValue {
  const context = useContext(SettlementContext);
  if (!context) {
    throw new Error('useSettlement must be used within SettlementProvider');
  }
  return context;
}

interface SettlementProviderProps {
  estateId: string | null;
  children: ReactNode;
}

export const SettlementProvider: React.FC<SettlementProviderProps> = ({ estateId, children }) => {
  const [estate, setEstate] = useState<SettlementEstateInfo | null>(null);
  const [coExecutors, setCoExecutors] = useState<CoExecutor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isArchived, setIsArchived] = useState(false);
  const [settledAt, setSettledAt] = useState<string | null>(null);
  const [canClose, setCanClose] = useState(false);
  const [completionChecks, setCompletionChecks] = useState<CompletionChecks | null>(null);
  const [closureMissing, setClosureMissing] = useState<string[]>([]);

  const fetchEstateInfo = useCallback(async () => {
    if (!estateId) {
      setEstate(null);
      setCoExecutors([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch estate overview from existing endpoint
      const response = await apiService.request(`/api/settlement/${estateId}`, {
        method: 'GET',
      });

      if (response.success || response.estateOwner) {
        const deathCertStatus: DeathCertStatus =
          response.deathCertStatus === 'approved' ? 'approved' :
          response.deathCertStatus === 'pending' ? 'pending' :
          response.deathCertStatus === 'rejected' ? 'rejected' : 'none';

        // Determine if planner path (user existed before settlement) or clean slate
        const path: SettlementPath = response.estateOwner?.email?.includes('deceased+')
          ? 'clean-slate'
          : 'planner';

        setEstate({
          estateId,
          ownerName: response.estateOwner?.name || 'Unknown',
          ownerEmail: response.estateOwner?.email,
          dateOfPassing: response.dateOfPassing,
          path,
          deathCertStatus,
        });

        // Fetch co-executors
        if (response.executors) {
          setCoExecutors(
            response.executors.map((ex: { userId: string; name: string; email: string; lastActive?: string }) => ({
              userId: ex.userId,
              name: ex.name,
              email: ex.email,
              lastActive: ex.lastActive,
            }))
          );
        }
      } else {
        setError(response.message || 'Failed to load estate information');
      }
    } catch (err) {
      console.error('[SettlementContext] Failed to fetch estate info:', err);
      setError(err instanceof Error ? err.message : 'Failed to load estate');
    } finally {
      setIsLoading(false);
    }
  }, [estateId]);

  const fetchClosure = useCallback(async () => {
    if (!estateId) {
      setIsArchived(false);
      setSettledAt(null);
      setCanClose(false);
      setCompletionChecks(null);
      setClosureMissing([]);
      return;
    }
    try {
      const res = await apiService.request(`/api/settlement/${estateId}/completion-status`, { method: 'GET' });
      setIsArchived(!!res.isArchived);
      setSettledAt(res.settledAt ? String(res.settledAt) : null);
      setCanClose(!!res.canClose);
      setCompletionChecks((res.checks as CompletionChecks) || null);
      setClosureMissing(Array.isArray(res.missing) ? res.missing : []);
    } catch {
      setIsArchived(false);
      setSettledAt(null);
      setCanClose(false);
      setCompletionChecks(null);
      setClosureMissing([]);
    }
  }, [estateId]);

  useEffect(() => {
    fetchEstateInfo();
  }, [fetchEstateInfo]);

  useEffect(() => {
    void fetchClosure();
  }, [fetchClosure]);

  const isDeathCertApproved = estate?.deathCertStatus === 'approved';
  const isPlannerPath = estate?.path === 'planner';

  return (
    <SettlementContext.Provider
      value={{
        estate,
        coExecutors,
        isLoading,
        error,
        refresh: fetchEstateInfo,
        isDeathCertApproved,
        isPlannerPath,
        isArchived,
        settledAt,
        canClose,
        completionChecks,
        closureMissing,
        refreshClosure: fetchClosure,
      }}
    >
      {children}
    </SettlementContext.Provider>
  );
};
